/**
 * Cognito, owned by the web client.
 *
 * This is the one piece of "behaviour" the browser keeps rather than pushing to the
 * server, because it is not app logic — it is a vendor SDK doing a standard auth dance,
 * and proxying it through our own API would only add a hop that can fail.
 *
 * Tokens are held in localStorage. That is forced, not chosen: amazon-cognito-identity-js
 * requires a SYNCHRONOUS storage, and given an async one it silently falls back to memory
 * and signs the user out on every reload. The mitigation is the CSP and the dedicated
 * origin (see infra/lib/saddle-web-stack.ts), not a different store.
 *
 * The ACCESS token is what every Saddle origin wants — the API verifies token_use ===
 * 'access' and rejects an ID token with a 401 even though the same pool signed it.
 */
import {
  AuthenticationDetails,
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
  CognitoUserSession,
  type ICognitoStorage,
} from 'amazon-cognito-identity-js';

export interface SessionUser {
  id: string;
  email: string;
}

export interface CognitoSettings {
  userPoolId: string;
  clientId: string;
}

/**
 * The SAME storage the shared CognitoService writes through.
 *
 * Not a second localStorage wrapper. `@/storage/mmkv` is aliased to platform/mmkv.web.ts
 * for the browser, and that shim TYPE-TAGS what it stores — a token is written as
 * `s:<jwt>`. A wrapper reading window.localStorage directly would get the tag back as part
 * of the value: a corrupted JWT, a 401 on every call, and a sign-in loop. Two wrappers
 * over one session is how the last sign-out bug happened; this is one implementation with
 * two consumers, until the screens stop using CognitoService entirely.
 */
import { mmkv, storage as kv } from '../platform/mmkv.web';

const COGNITO_PREFIX = 'CognitoIdentityServiceProvider.';

const storage: ICognitoStorage = {
  getItem: (key) => mmkv.getString(key) ?? null,
  setItem: (key, value) => mmkv.setString(key, value),
  removeItem: (key) => {
    mmkv.delete(key);
  },
  /** Only Cognito's own keys — never the rest of the app's data. */
  clear: () => {
    kv.getAllKeys()
      .filter((key) => key.startsWith(COGNITO_PREFIX))
      .forEach((key) => kv.remove(key));
  },
};

let settings: CognitoSettings = { userPoolId: '', clientId: '' };
let pool: CognitoUserPool | null = null;

export function configureAuth(next: CognitoSettings): void {
  settings = next;
  pool = null;
}

/** Null when auth is not configured, rather than throwing on an empty pool id. */
function userPool(): CognitoUserPool | null {
  if (pool) return pool;
  if (!settings.userPoolId || !settings.clientId) return null;
  pool = new CognitoUserPool({
    UserPoolId: settings.userPoolId,
    ClientId: settings.clientId,
    Storage: storage,
  });
  return pool;
}

export function authConfigured(): boolean {
  return Boolean(settings.userPoolId && settings.clientId);
}

/**
 * An email as the pool should see it: trimmed and lowercased, as the phone's
 * `normalizeIdentifier` does.
 *
 * The pool is case-SENSITIVE (UsernameConfiguration is null), and the phone and the published
 * sites' sign-up page share it with this client. Only trimmed here, "Me@x.com" typed in the
 * browser and "me@x.com" typed on the phone could be two different Saddle accounts — or one
 * account that each client could not find.
 */
export function normalizeEmail(raw: string): string {
  return raw.trim().toLowerCase();
}

/**
 * The pool's UsernameAttributes is email/phone, so the EMAIL is the username. Passing a
 * generated one throws InvalidParameterException — a regression this product has shipped
 * before. Taken exactly as given: which spelling to ask for is each caller's decision.
 */
function cognitoUser(username: string): CognitoUser {
  const active = userPool();
  if (!active) throw new Error('Auth is not configured.');
  // Same synchronous storage as the pool: with the SDK's async default, sign-in would
  // cache tokens where the pool's getCurrentUser() cannot read them on a cold start.
  return new CognitoUser({ Username: username, Pool: active, Storage: storage });
}

/** Cognito's error name survives the rethrow, so a caller can tell the failures apart. */
type AuthFailure = Error & { code?: string };

function failure(err: AuthFailure): AuthFailure {
  const out: AuthFailure = new Error(err.message);
  out.code = err.code ?? err.name;
  return out;
}

const codeOf = (err: unknown): string | undefined => (err as AuthFailure | null)?.code;

/**
 * Ask for the account lowercased and — only when the person typed capitals and the pool answered
 * as though there were no such account — once more exactly as typed.
 *
 * Accounts this browser created before sign-up lowercased are stored as they were typed, and the
 * pool is case-sensitive: "Me@x.com" is only ever found as "Me@x.com". Lowercasing alone would
 * have locked every one of them out. The pool hides whether an account exists
 * (PreventUserExistenceErrors), so "no such account" and "wrong password" (or "wrong code") are
 * one answer; a wrong one fails both spellings the same way, and the FIRST refusal is reported.
 * A different answer to the second (not confirmed, say) is about the account that exists.
 */
async function eitherSpelling<T>(
  email: string,
  act: (username: string) => Promise<T>,
  asIfMissing: (err: unknown) => boolean,
): Promise<T> {
  const username = normalizeEmail(email);
  try {
    return await act(username);
  } catch (first) {
    const typed = email.trim();
    if (typed === username || !asIfMissing(first)) throw first;
    try {
      return await act(typed);
    } catch (second) {
      throw asIfMissing(second) ? first : second;
    }
  }
}

function userFrom(session: CognitoUserSession): SessionUser {
  const payload = session.getIdToken().decodePayload() as { sub?: string; email?: string };
  return { id: payload.sub ?? '', email: payload.email ?? '' };
}

export async function signUp(
  email: string,
  password: string,
  opts: { acceptedTermsAt?: string } = {},
): Promise<void> {
  const active = userPool();
  if (!active) throw new Error('Auth is not configured.');
  const username = normalizeEmail(email);
  const attributes = [
    new CognitoUserAttribute({ Name: 'email', Value: username }),
    new CognitoUserAttribute({
      Name: 'custom:terms_accepted_at',
      Value: opts.acceptedTermsAt ?? new Date().toISOString(),
    }),
  ];
  return new Promise((resolve, reject) => {
    // `failure` keeps Cognito's name: UsernameExistsException is where Sign up offers the code.
    active.signUp(username, password, attributes, [], (err) =>
      err ? reject(failure(err as AuthFailure)) : resolve(),
    );
  });
}

/**
 * Confirm and resend follow a sign-up, which now always stores the account lowercased; the
 * Confirm screen carries the email as it was typed, so they lowercase it too.
 */
export async function confirmSignUp(email: string, code: string): Promise<void> {
  return new Promise((resolve, reject) => {
    cognitoUser(normalizeEmail(email)).confirmRegistration(code.trim(), true, (err) =>
      err ? reject(failure(err as AuthFailure)) : resolve(),
    );
  });
}

export async function resendConfirmation(email: string): Promise<void> {
  return new Promise((resolve, reject) => {
    cognitoUser(normalizeEmail(email)).resendConfirmationCode((err) => (err ? reject(failure(err as AuthFailure)) : resolve()));
  });
}

function authenticate(username: string, password: string): Promise<SessionUser> {
  const user = cognitoUser(username);
  return new Promise((resolve, reject) => {
    user.authenticateUser(new AuthenticationDetails({ Username: username, Password: password }), {
      onSuccess: (session) => resolve(userFrom(session)),
      onFailure: (err: AuthFailure) => reject(failure(err)),
      newPasswordRequired: () => reject(new Error('This account needs a new password. Please contact support.')),
    });
  });
}

export async function signIn(email: string, password: string): Promise<SessionUser> {
  // A wrong email and a wrong password are one answer: the pool hides which.
  return eitherSpelling(email, (username) => authenticate(username, password), (err) =>
    codeOf(err) === 'NotAuthorizedException' || codeOf(err) === 'UserNotFoundException',
  );
}

export async function signOut(): Promise<void> {
  userPool()?.getCurrentUser()?.signOut();
  storage.clear();
}

function requestReset(username: string): Promise<void> {
  return new Promise((resolve, reject) => {
    cognitoUser(username).forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err: Error) => reject(new Error(err.message)),
      inputVerificationCode: () => resolve(),
    });
  });
}

/**
 * Send a reset code to the account, lowercased — and, when the person typed capitals, to the
 * spelling they typed as well.
 *
 * Unlike sign-in there is no refusal to fall back on: the pool answers a reset for an account
 * that does not exist exactly as it answers one that does (PreventUserExistenceErrors), so an
 * account made here before sign-up lowercased would never get its code and nobody would be told.
 * Asking for both spellings costs nothing: only the account that exists is sent a code.
 */
export async function forgotPassword(email: string): Promise<void> {
  const username = normalizeEmail(email);
  await requestReset(username);
  const typed = email.trim();
  if (typed !== username) await requestReset(typed);
}

export async function confirmForgotPassword(email: string, code: string, newPassword: string): Promise<void> {
  // A code checked against an account that does not exist reads as a wrong or spent code.
  return eitherSpelling(
    email,
    (username) =>
      new Promise<void>((resolve, reject) => {
        cognitoUser(username).confirmPassword(code.trim(), newPassword, {
          onSuccess: () => resolve(),
          onFailure: (err: AuthFailure) => reject(failure(err)),
        });
      }),
    (err) => ['CodeMismatchException', 'ExpiredCodeException', 'UserNotFoundException'].includes(codeOf(err) ?? ''),
  );
}

/** Resolves the stored session, refreshing it if the SDK decides it must. */
export async function currentUser(): Promise<SessionUser | null> {
  const user = userPool()?.getCurrentUser();
  if (!user) return null;
  return new Promise((resolve) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session?.isValid()) {
        storage.clear();
        resolve(null);
        return;
      }
      resolve(userFrom(session));
    });
  });
}

/**
 * Whose session the next request will carry — the `sub` the stored tokens belong to, or null.
 *
 * Unlike `currentUser`, a failure clears nothing: this is asked in the middle of a build (whether
 * the account that started it is still the one signed in), and a refresh that fails on a flaky
 * connection must not sign the person out as a side effect of asking.
 */
export function signedInUserId(): Promise<string | null> {
  const user = userPool()?.getCurrentUser();
  if (!user) return Promise.resolve(null);
  return new Promise((resolve) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      resolve(!err && session?.isValid() ? userFrom(session).id || null : null);
    });
  });
}

/**
 * The access token, refreshed first when it has expired.
 *
 * It was read SYNCHRONOUSLY, on the grounds that the SDK's session callback fires at once when
 * the session is cached. It does — while the token is fresh. After its hour, `getSession` starts
 * a refresh over the network and calls back when that lands, and the synchronous read had already
 * returned null: a Build or a chat edit sent after an idle hour went out with no token and came
 * back "Your session has expired" to somebody whose refresh token was good for a month (F16,
 * 2026-09-30). Awaited now, so the request waits for the refresh instead.
 */
export function accessToken(): Promise<string | null> {
  const user = userPool()?.getCurrentUser();
  if (!user) return Promise.resolve(null);
  return new Promise((resolve) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      resolve(!err && session?.isValid() ? session.getAccessToken().getJwtToken() : null);
    });
  });
}

/**
 * The signed-in account's id (the token's `sub`), read SYNCHRONOUSLY from the stored session.
 *
 * Who is signed in, never whether their token is fresh. The project store asks this on every
 * read and write to refuse another account's projects (platform/projectStore.server.ts), and it
 * must answer at once. It was wired as `subOfToken(accessToken())` — written against the old
 * synchronous accessToken just as F16 made it async, so it decoded a Promise and every answer was
 * null: no cross-account guard at all (merge of lane/journey and lane/projects, 2026-09-30). The
 * tests mocked accessToken as synchronous and passed. An expired token still names its account,
 * so the stored one is read as it is; the request itself still waits for `accessToken()`.
 */
export function signedInSub(): string | null {
  const active = userPool();
  const user = active?.getCurrentUser();
  if (!active || !user) return null;
  const token = storage.getItem(`${COGNITO_PREFIX}${active.getClientId()}.${user.getUsername()}.accessToken`);
  const part = token?.split('.')[1];
  if (!part) return null;
  try {
    const padded = part.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(part.length / 4) * 4, '=');
    const payload = JSON.parse(atob(padded)) as { sub?: unknown };
    return typeof payload.sub === 'string' && payload.sub ? payload.sub : null;
  } catch {
    return null;
  }
}

/**
 * A new access token, whatever the cached one says — for a request the server answered 401.
 *
 * A token this browser believes is fresh can still be refused: a device clock running slow, or a
 * token revoked elsewhere. The refresh token decides; null when there is no session to renew, or
 * it will not renew, and then the 401 stands and the person is asked to sign in.
 */
export function refreshAccessToken(): Promise<string | null> {
  const user = userPool()?.getCurrentUser();
  if (!user) return Promise.resolve(null);
  return new Promise((resolve) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session) {
        resolve(null);
        return;
      }
      user.refreshSession(session.getRefreshToken(), (refreshErr: Error | null, renewed: CognitoUserSession | null) => {
        resolve(!refreshErr && renewed?.isValid() ? renewed.getAccessToken().getJwtToken() : null);
      });
    });
  });
}

export async function deleteAccount(): Promise<void> {
  const user = userPool()?.getCurrentUser();
  if (!user) throw new Error('You are not signed in.');
  await new Promise<void>((resolve, reject) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session?.isValid()) {
        reject(new Error('Your session has expired. Please sign in again.'));
        return;
      }
      user.deleteUser((delErr) => {
        if (delErr) {
          reject(new Error(delErr.message));
          return;
        }
        storage.clear();
        resolve();
      });
    });
  });
}

/*
 * --- Sign-up and sign-in by an emailed code, no password (easy-signup handoff, 2026-10-04) ---
 *
 * amazon-cognito-identity-js predates Cognito's choice-based sign-in (USER_AUTH, EMAIL_OTP), so
 * these four calls go to Cognito's API directly — the same public endpoint the SDK uses, the same
 * client id, no credentials of ours. What they end with is put where the SDK keeps a session
 * (`adoptTokens`), so everything after — refresh, the token on every request, sign-out — is the
 * SDK's as before.
 */

/** Tokens as Cognito hands them back, from a code or from the OAuth token endpoint. */
export interface Tokens {
  IdToken: string;
  AccessToken: string;
  RefreshToken?: string;
}

/** What `startEmail` set going: a new account's confirmation code, or a sign-in code. */
export type EmailStart =
  | { mode: 'signup' }
  | { mode: 'signin'; session: string };

/** Overridable by tests: the one network call these make. */
let fetchImpl: typeof fetch = (...a) => fetch(...a);
export function _setAuthFetch(f: typeof fetch): void {
  fetchImpl = f;
}

async function cognito<T>(action: string, body: Record<string, unknown>): Promise<T> {
  const region = settings.userPoolId.split('_')[0];
  if (!region || !settings.clientId) throw new Error('Auth is not configured.');
  const res = await fetchImpl(`https://cognito-idp.${region}.amazonaws.com/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': `AWSCognitoIdentityProviderService.${action}`,
    },
    body: JSON.stringify({ ClientId: settings.clientId, ...body }),
  });
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    // `__type` is "UsernameExistsException", or prefixed "com.amazon…#UsernameExistsException".
    const type = String(data.__type ?? 'Error').split('#').pop() ?? 'Error';
    throw failure(Object.assign(new Error(String(data.message ?? data.Message ?? 'Something went wrong.')), { code: type }));
  }
  return data as T;
}

/** The payload of a JWT, unverified: only to read our own token's claims. */
export function claimsOf(jwt: string): Record<string, unknown> {
  const part = jwt.split('.')[1] ?? '';
  try {
    const padded = part.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(part.length / 4) * 4, '=');
    return JSON.parse(atob(padded)) as Record<string, unknown>;
  } catch {
    return {};
  }
}

/**
 * Keep a session from tokens obtained outside the SDK, exactly where the SDK keeps its own: under
 * the account's real username (the access token's `username` — a uuid here, or `google_…`), so
 * `getCurrentUser()` finds it on the next load and refreshes it when it expires.
 */
export function adoptTokens(tokens: Tokens): SessionUser {
  const username = String(claimsOf(tokens.AccessToken).username ?? '');
  if (!username) throw new Error('That sign-in did not come back complete. Please try again.');
  const session = new CognitoUserSession({
    IdToken: new CognitoIdToken({ IdToken: tokens.IdToken }),
    AccessToken: new CognitoAccessToken({ AccessToken: tokens.AccessToken }),
    RefreshToken: new CognitoRefreshToken({ RefreshToken: tokens.RefreshToken ?? '' }),
  });
  cognitoUser(username).setSignInUserSession(session);
  return userFrom(session);
}

/** A sign-in code for an account that exists: Cognito's EMAIL_OTP challenge, and its session. */
async function requestSignInCode(username: string): Promise<string> {
  let res = await cognito<{ ChallengeName?: string; Session?: string }>('InitiateAuth', {
    AuthFlow: 'USER_AUTH',
    AuthParameters: { USERNAME: username, PREFERRED_CHALLENGE: 'EMAIL_OTP' },
  });
  // Offered a choice rather than the code: choose the code.
  if (res.ChallengeName === 'SELECT_CHALLENGE') {
    res = await cognito('RespondToAuthChallenge', {
      ChallengeName: 'SELECT_CHALLENGE',
      Session: res.Session,
      ChallengeResponses: { USERNAME: username, ANSWER: 'EMAIL_OTP' },
    });
  }
  if (res.ChallengeName !== 'EMAIL_OTP' || !res.Session) {
    throw failure(Object.assign(new Error('We could not send a sign-in code to that email.'), { code: 'NoEmailCode' }));
  }
  return res.Session;
}

/**
 * "Continue with email": a new account, or a code to sign in to the one that exists.
 *
 * Sign-up first, with no password: an email nobody has used gets an account and a confirmation
 * code. One that has an account gets a sign-in code instead — confirmed accounts by Cognito's
 * EMAIL_OTP, and one that was never confirmed a fresh confirmation code, since that is what it
 * still needs. The person sees one thing either way: a code to type.
 */
export async function startEmail(email: string, opts: { acceptedTermsAt?: string } = {}): Promise<EmailStart> {
  const username = normalizeEmail(email);
  try {
    await cognito('SignUp', {
      Username: username,
      UserAttributes: [
        { Name: 'email', Value: username },
        { Name: 'custom:terms_accepted_at', Value: opts.acceptedTermsAt ?? new Date().toISOString() },
      ],
    });
    return { mode: 'signup' };
  } catch (err) {
    if (codeOf(err) !== 'UsernameExistsException') throw err;
  }
  try {
    return { mode: 'signin', session: await requestSignInCode(username) };
  } catch (err) {
    if (codeOf(err) !== 'UserNotConfirmedException') throw err;
    await cognito('ResendConfirmationCode', { Username: username });
    return { mode: 'signup' };
  }
}

/** A fresh code for the same purpose. A sign-in code needs a new session; a sign-up's does not. */
export async function resendEmailCode(email: string, mode: EmailStart['mode']): Promise<EmailStart> {
  const username = normalizeEmail(email);
  if (mode === 'signin') return { mode, session: await requestSignInCode(username) };
  await cognito('ResendConfirmationCode', { Username: username });
  return { mode };
}

/**
 * The code typed, and the session that comes of it.
 *
 * A sign-up's code confirms the account, and Cognito answers with a session that signs it in at
 * once (USER_AUTH with that session) — no password, no second code. If that session is missing,
 * `needsSignInCode` says so and a sign-in code is on its way instead.
 */
export async function finishEmail(
  email: string,
  code: string,
  pending: EmailStart,
): Promise<{ user: SessionUser; created: boolean } | { needsSignInCode: string }> {
  const username = normalizeEmail(email);
  const entered = code.trim();
  if (pending.mode === 'signin') {
    const res = await cognito<{ AuthenticationResult?: Tokens }>('RespondToAuthChallenge', {
      ChallengeName: 'EMAIL_OTP',
      Session: pending.session,
      ChallengeResponses: { USERNAME: username, EMAIL_OTP_CODE: entered },
    });
    if (!res.AuthenticationResult) throw new Error('That code was not accepted.');
    return { user: adoptTokens(res.AuthenticationResult), created: false };
  }
  const confirmed = await cognito<{ Session?: string }>('ConfirmSignUp', { Username: username, ConfirmationCode: entered });
  if (confirmed.Session) {
    const res = await cognito<{ AuthenticationResult?: Tokens }>('InitiateAuth', {
      AuthFlow: 'USER_AUTH',
      AuthParameters: { USERNAME: username },
      Session: confirmed.Session,
    });
    if (res.AuthenticationResult) return { user: adoptTokens(res.AuthenticationResult), created: true };
  }
  return { needsSignInCode: await requestSignInCode(username) };
}
